import { useState, useCallback, useRef, useLayoutEffect } from "react";
import { useLongPress } from "@/hooks/useLongPress";
import { TapbackOverlay } from "./TapbackOverlay";
import { TapbackBadges } from "./TapbackBadges";
import { WhoReactedSheet } from "./WhoReactedSheet";

const findBubble = (root) => root?.querySelector('[class*="rounded-2xl"]') || root?.firstElementChild || root;

// Wraps a message: long-press lifts the bubble and shows the Tapback pill; reactions pin to the bubble corner.
export const ReactableMessage = ({ msg, meType, meId, align = "left", isLight = true, onReact, children }) => {
  const [open, setOpen] = useState(false);
  const [whoOpen, setWhoOpen] = useState(false);
  const showWho = useCallback(() => {
    if (navigator.vibrate) navigator.vibrate(10);
    setWhoOpen(true);
  }, []);
  const [bubbleEl, setBubbleEl] = useState(null);
  const [anchor, setAnchor] = useState(null);
  const wrapRef = useRef(null);
  const hasReactions = (msg.reactions || []).length > 0;

  useLayoutEffect(() => {
    if (!open && !whoOpen) return;
    document.documentElement.classList.add("tc-no-select");
    return () => document.documentElement.classList.remove("tc-no-select");
  }, [open, whoOpen]);

  const openPicker = useCallback(() => {
    try { window.getSelection()?.removeAllRanges(); } catch { /* ignore */ }
    setBubbleEl(findBubble(wrapRef.current));
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const press = useLongPress(openPicker);
  const mine = (msg.reactions || []).find((r) => r.by_type === meType && r.by_id === meId)?.emoji;

  useLayoutEffect(() => {
    if (!hasReactions || !wrapRef.current) return;
    const measure = () => {
      const w = wrapRef.current;
      const b = findBubble(w);
      if (!w || !b) return;
      const wr = w.getBoundingClientRect();
      const br = b.getBoundingClientRect();
      setAnchor({ top: br.top - wr.top, left: br.left - wr.left, width: br.width });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [hasReactions, msg.reactions, children]);

  const pick = (emoji) => {
    setOpen(false);
    onReact(msg.id, emoji);
  };

  return (
    <div ref={wrapRef} className={`relative w-full ${hasReactions ? "mt-3" : ""}`} data-testid={`message-${msg.id}`}>
      <div {...press} className={`flex w-full ${align === "right" ? "justify-end" : "justify-start"} select-none`} style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }} data-testid={`message-bubble-${msg.id}`}>
        {children}
      </div>
      {hasReactions && anchor && (
        <div className="absolute pointer-events-none" style={{ top: anchor.top, left: anchor.left, width: anchor.width, height: 0 }}>
          <div className="relative pointer-events-auto">
            <TapbackBadges reactions={msg.reactions} meType={meType} meId={meId} align={align} isLight={isLight} onToggle={(e) => onReact(msg.id, e)} onShowWho={showWho} />
          </div>
        </div>
      )}
      <WhoReactedSheet
        open={whoOpen && hasReactions}
        reactions={msg.reactions}
        meType={meType}
        meId={meId}
        isLight={isLight}
        onClose={() => setWhoOpen(false)}
        onRemoveMine={(emoji) => { setWhoOpen(false); onReact(msg.id, emoji); }}
      />
      <TapbackOverlay open={open} bubbleEl={bubbleEl} align={align} current={mine} onPick={pick} onClose={close} isLight={isLight} />
    </div>
  );
};

export const applyReactions = (messages, msgId, reactions) => messages.map((m) => (m.id === msgId ? { ...m, reactions } : m));
